/**
 * Automatisk tilldelning (molnfast-react#1653): client for the
 * `autoAssignmentApi` callable (admin or superadmin). The callable writes
 * `global/autoAssignment/issues` and, when enabled, ensures the förvaltare
 * has an active permanent guest link; clients never write the node.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { recordActivity } from './activityApi';
import {
    AutoAssignmentApiRequest,
    AutoAssignmentApiResponse,
    IssueAutoAssignmentState,
    SaveIssueAutoAssignmentPayload,
} from '../types/autoAssignment';

export const THRESHOLD_MAX = 10_000_000;

/** Swedish message for an invalid form, or null when it can be sent. */
export const validateIssueAutoAssignment = (payload: SaveIssueAutoAssignmentPayload): string | null => {
    if (!Number.isInteger(payload.threshold) || payload.threshold < 1 || payload.threshold > THRESHOLD_MAX) {
        return 'Beloppsgränsen ska vara ett heltal mellan 1 och 10 000 000 kr';
    }
    if (payload.enabled && !payload.externalUserId) {
        return 'Välj en förvaltare';
    }
    return null;
};

class AutoAssignmentApi {
    private functions = getFunctions(undefined, 'europe-north1');

    private async call(request: AutoAssignmentApiRequest): Promise<IssueAutoAssignmentState> {
        const callable = httpsCallable<AutoAssignmentApiRequest, AutoAssignmentApiResponse>(this.functions, 'autoAssignmentApi');
        const result = await callable(request);
        return { setting: result.data.setting ?? null, link: result.data.link ?? null };
    }

    /** The felanmälan setting and the förvaltare's current link. */
    async getIssues(): Promise<IssueAutoAssignmentState> {
        return this.call({ action: 'getIssues' });
    }

    /** Save the felanmälan setting; when enabled the förvaltare gets a permanent link. */
    async saveIssues(payload: SaveIssueAutoAssignmentPayload): Promise<IssueAutoAssignmentState> {
        const invalid = validateIssueAutoAssignment(payload);
        if (invalid) throw new Error(invalid);
        const state = await this.call({
            action: 'saveIssues',
            enabled: payload.enabled,
            ...(payload.externalUserId ? { externalUserId: payload.externalUserId } : {}),
            threshold: payload.threshold,
            notifyUids: payload.notifyUids,
        });
        recordActivity('settings.updated', { label: 'Automatisk tilldelning' });
        return state;
    }
}

export const autoAssignmentApi = new AutoAssignmentApi();
